import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useEffect } from 'react';
import { brandValues, publishingDirections, siteInfo } from '@/mocks/brand';
import useScrollAnimation from '@/hooks/useScrollAnimation';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/999b20db-023a-415b-8938-ab1563276c79/adab1eea-aa1c-4be9-9113-c915a6c79d56_compressed_-01.webp';

const HERO_VIDEO_URL = 'https://public.readdy.ai/ai/video_res/01a0ebd1-6fb0-7581-8344-66a3a4242ce9.mp4';

function AboutHero() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden bg-background-50"
    >
      <div className="absolute inset-0 z-0">
        <video
          src={HERO_VIDEO_URL}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
      </div>

      <div
        className={`relative z-10 flex flex-col items-center text-center px-6 md:px-10 w-full max-w-[720px] transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <img
          src={LOGO_URL}
          alt={siteInfo.brandName}
          className="w-20 h-20 md:w-24 md:h-24 object-contain mx-auto mb-6 drop-shadow-lg"
        />
        <h1
          className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2"
          style={{ textShadow: '0 2px 12px rgba(0,0,0,0.50), 0 1px 4px rgba(0,0,0,0.30)' }}
        >
          關於沐燁文化
        </h1>
        <p
          className="text-xs md:text-sm text-background-50/50 tracking-[0.25em] uppercase mb-6"
          style={{ textShadow: '0 1px 4px rgba(0,0,0,0.30)' }}
        >
          {siteInfo.englishName}
        </p>
        <div className="space-y-3 mb-8 text-left w-full max-w-[560px]">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-accent-400 mt-0.5 shrink-0">
              <i className="ri-building-line text-sm"></i>
            </span>
            <p
              className="text-background-50/85 text-sm md:text-base leading-relaxed"
              style={{ textShadow: '0 1px 6px rgba(0,0,0,0.45)' }}
            >
              沐燁文化委託崧博出版經銷，官方網站由崧博出版製作。本頁提供沐燁文化的品牌介紹、正式出版內容，以及書籍資料庫與合作購書通路入口。
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-accent-400/70 mt-0.5 shrink-0">
              <i className="ri-file-text-line text-sm"></i>
            </span>
            <p
              className="text-background-50/65 text-sm md:text-base leading-relaxed"
              style={{ textShadow: '0 1px 6px rgba(0,0,0,0.35)' }}
            >
              網站所呈現的書名、作者、內容簡介、ISBN與出版日期，均應以正式公開資料為準。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutContent() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center bg-background-100 overflow-hidden py-20 md:py-0"
    >
      <div
        className={`relative z-10 flex flex-col items-center px-6 md:px-10 w-full max-w-[760px] transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8 text-center">
          沐燁文化的品牌定位
        </h2>
        <div className="space-y-4 text-left w-full">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
              <i className="ri-book-3-line text-sm"></i>
            </span>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              沐燁文化 MOYA 以 AIGC 彩色書籍與史密森學會相關科普內容為主要出版方向，透過品牌網站呈現書籍介紹、出版資訊與合作購書通路，協助讀者認識目前公開的出版作品，並依個人興趣與閱讀需求查找相關內容。
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
              <i className="ri-lightbulb-line text-sm"></i>
            </span>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              沐燁文化 MOYA 關注出版內容所傳遞的知識、觀點與閱讀價值，透過圖像與文字的結合，提供具視覺閱讀特色的科普出版內容，並以清楚的書籍資訊與書目入口降低讀者查找出版內容的困難。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutInfo() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center bg-primary-900 overflow-hidden py-20 md:py-0"
    >
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, oklch(0.72 0.15 85) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="relative z-10 flex flex-col items-center text-center px-6 md:px-10 w-full">
        <div
          className={`max-w-[720px] w-full transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            沐燁文化 MOYA 與崧博出版
          </h2>
          <p className="text-background-200/70 text-sm md:text-base leading-relaxed max-w-[560px] mx-auto mb-12">
            沐燁文化 MOYA 以 AIGC 彩色書籍與史密森學會相關科普內容為主要出版方向。本品牌委託崧博出版經銷，官方網站由崧博出版製作。讀者可透過沐燁文化品牌網站瀏覽相關出版內容，並前往崧博出版書籍資料庫查詢完整書目資訊。
          </p>
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 max-w-[960px] w-full transition-all duration-700 ease-out delay-150 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {brandValues.map((v) => (
            <div
              key={v.id}
              className="flex items-start gap-4 p-6 bg-primary-800/40 border border-primary-700/30 rounded-xl text-left"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-accent-400 shrink-0">
                <i className={`${v.icon} text-lg`}></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-background-50 mb-2">
                  {v.title}
                </h3>
                <p className="text-sm text-background-200/60 leading-relaxed">
                  {v.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutGuide() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center bg-background-50 overflow-hidden py-20 md:py-0"
    >
      <div className="relative z-10 flex flex-col items-center text-center px-6 md:px-10 w-full">
        <div
          className={`max-w-[720px] w-full transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            查詢沐燁文化正式書目
          </h2>
          <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-[560px] mx-auto mb-10">
            完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
          </p>
        </div>

        <div
          className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 max-w-[960px] w-full transition-all duration-700 ease-out delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {publishingDirections.map((d) => (
            <div
              key={d.name}
              className="flex flex-col items-center text-center p-5 bg-background-100 border border-background-200/70 rounded-xl"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-3">
                <i className={`${d.icon} text-lg`}></i>
              </div>
              <span className="text-sm font-medium text-foreground-800">
                {d.name}
              </span>
            </div>
          ))}
        </div>
        <div
          className={`mt-8 transition-all duration-700 ease-out delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <a
            href={siteInfo.catalogUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            查詢完整書目
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-external-link-line text-xs"></i>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

function AboutContact() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center bg-background-100 overflow-hidden py-20 md:py-0"
    >
      <div
        className={`relative z-10 flex flex-col items-center text-center px-6 md:px-10 w-full max-w-[640px] transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
          品牌經銷與合作洽詢
        </h2>
        <div className="space-y-3 text-left w-full mb-8">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
              <i className="ri-service-line text-sm"></i>
            </span>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              如有沐燁文化品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
              <i className="ri-mail-line text-sm"></i>
            </span>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              品牌經銷與合作方案詳情請直接聯繫洽詢。
            </p>
          </div>
        </div>
        <a
          href={siteInfo.parentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          前往崧博出版
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-external-link-line text-xs"></i>
          </span>
        </a>
      </div>
    </section>
  );
}

export default function AboutPage() {
  useEffect(() => {
    document.title = '關於沐燁文化｜品牌介紹與出版資訊';
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <AboutHero />
        <AboutContent />
        <AboutInfo />
        <AboutGuide />
        <AboutContact />
      </main>
      <Footer />
    </>
  );
}